import { Injectable, inject as inject_1 } from '@angular/core';
import * as jsts from 'jsts';
import GeoJSON from 'ol/format/GeoJSON';
import {
  LineString,
  LinearRing,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
  Geometry,
} from 'ol/geom';
import { Feature } from 'src/app/geo/models';

import { DivideGeometryResult, DivideValidation } from '../models';
import { JstsHelperService } from './jsts-helper.service';

@Injectable({
  providedIn: 'root',
})
export class DivideGeometryService {
  private readonly jstsHelper = inject_1(JstsHelperService);

  divide(fieldFeature: Feature, cutFeature: Feature): DivideGeometryResult {
    let field1Geom: jsts.geom.Geometry = null;
    let field2Geom: jsts.geom.Geometry = null;
    let producesTwoFields = false;
    let hasCorrectNumberOfGeometries = false;
    const format = new GeoJSON({});
    const features = format.readFeatures({
      type: 'FeatureCollection',
      features: [fieldFeature, cutFeature],
    });
    const parser = new jsts.io.OL3Parser();
    // @ts-ignore: ts doesn't have an up-to-date type for parser instance
    parser.inject(
      Point,
      LineString,
      LinearRing,
      Polygon,
      MultiPoint,
      MultiLineString,
      MultiPolygon,
    );

    const fieldGeom = parser.read(features[0].getGeometry());
    const cutGeom = parser.read(features[1].getGeometry());
    const isIntersecting = this.jstsHelper.safeIntersects(fieldGeom, cutGeom);

    try {
      field1Geom = fieldGeom.intersection(cutGeom);
      field2Geom = fieldGeom.difference(cutGeom);
    } catch (error) {
      console.error('Error during geometry division:', error);
    }
    const hasFieldGeom = !!field1Geom && !!field2Geom;
    producesTwoFields = hasFieldGeom && !!field1Geom.getNumPoints() && !!field2Geom.getNumPoints();
    hasCorrectNumberOfGeometries =
      hasFieldGeom && field1Geom.getNumGeometries() === 1 && field2Geom.getNumGeometries() === 1;
    const validation = !hasFieldGeom
      ? DivideValidation.invalidSelection
      : !isIntersecting
        ? DivideValidation.notIntersecting
        : !producesTwoFields
          ? DivideValidation.notProducingTwoFields
          : !hasCorrectNumberOfGeometries
            ? DivideValidation.wrongNumberOfGeometries
            : DivideValidation.success;

    return {
      validation,
      parser,
      format,
      field1Geom,
      field2Geom,
    };
  }

  getFeatures(result: DivideGeometryResult) {
    const parsedField1 = result.parser.write(result.field1Geom) as Geometry;
    const field1Geometry = result.format.writeGeometryObject(parsedField1);
    const parsedField2 = result.parser.write(result.field2Geom) as Geometry;
    const field2Geometry = result.format.writeGeometryObject(parsedField2);
    const defaultFeature: Feature = {
      id: null,
      cropId: null,
      properties: {},
      type: 'Feature',
      geometry: null,
    };

    return {
      feature1: {
        ...defaultFeature,
        geometry: field1Geometry as GeoJSON.Polygon,
      },
      feature2: {
        ...defaultFeature,
        geometry: field2Geometry as GeoJSON.Polygon,
      },
    };
  }
}
